import { useState } from "react";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import qs from "qs";
import { ApiConstant, AppConstant } from "const";
import { ApiUtils, HumpUtils, SystemUtils } from "utils";

type ActionType = "block" | "unblock" | "logout" | null;

interface ConfirmDialogState {
  open: boolean;
  type: ActionType;
  targetId: string | null;
}

export interface BulkParams {
  isSelectAllMode: boolean;
  excludeDeviceIds: string[];
  selectedDeviceIds: string[];
  searchText?: string;
  deviceTypeParam?: string | null;
  selectedVersions?: string[];
}

interface UseDeviceActionsOptions {
  onSuccess?: () => void;
}

function openSnackbar(message: string, variant: string) {
  window.dispatchEvent(
    new CustomEvent((AppConstant as any).CUSTOM_EVENTS.openSnackbar, {
      detail: { message, variant },
    })
  );
}

export default function useDeviceActions(options: UseDeviceActionsOptions = {}) {
  const { onSuccess } = options;
  const { t } = useTranslation("deviceManagement");

  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    open: false,
    type: null,
    targetId: null,
  });

  // --- Block mutation ---
  const blockMutation = useMutation({
    mutationFn: async (params: Record<string, unknown>) => {
      const response = await ApiUtils.createApiWithToken(false).post(
        ApiConstant.DEVICES_BLOCK,
        qs.stringify(HumpUtils.decamelizeKeys(params))
      );
      return response;
    },
    onMutate: () => {
      SystemUtils.openCircleLoading();
    },
    onSettled: () => {
      SystemUtils.openCircleLoading(false);
    },
    onSuccess: (response: any) => {
      if (response.status === ApiConstant.STT_OK) {
        openSnackbar(t("TXT_DEVICE_BLOCK_SUCCESS"), "success");
        onSuccess?.();
      } else {
        openSnackbar(t("TXT_DEVICE_BLOCK_FAIL"), "error");
      }
    },
    onError: () => {
      openSnackbar(t("TXT_DEVICE_BLOCK_ERROR"), "error");
    },
  });

  // --- Unblock mutation ---
  const unblockMutation = useMutation({
    mutationFn: async (params: Record<string, unknown>) => {
      const response = await ApiUtils.createApiWithToken(false).post(
        ApiConstant.DEVICES_UNBLOCK,
        qs.stringify(HumpUtils.decamelizeKeys(params))
      );
      return response;
    },
    onMutate: () => {
      SystemUtils.openCircleLoading();
    },
    onSettled: () => {
      SystemUtils.openCircleLoading(false);
    },
    onSuccess: (response: any) => {
      if (response.status === ApiConstant.STT_OK) {
        openSnackbar(t("TXT_DEVICE_UNBLOCK_SUCCESS"), "success");
        onSuccess?.();
      } else {
        openSnackbar(t("TXT_DEVICE_UNBLOCK_FAIL"), "error");
      }
    },
    onError: () => {
      openSnackbar(t("TXT_DEVICE_UNBLOCK_ERROR"), "error");
    },
  });

  // --- Logout mutation ---
  const logoutMutation = useMutation({
    mutationFn: async (params: Record<string, unknown>) => {
      const response = await ApiUtils.createApiWithToken(false).post(
        ApiConstant.DEVICES_LOGOUT,
        qs.stringify(HumpUtils.decamelizeKeys(params))
      );
      return response;
    },
    onMutate: () => {
      SystemUtils.openCircleLoading();
    },
    onSettled: () => {
      SystemUtils.openCircleLoading(false);
    },
    onSuccess: (response: any) => {
      if (response.status === ApiConstant.STT_OK) {
        openSnackbar(t("TXT_DEVICE_LOGOUT_SUCCESS"), "success");
        onSuccess?.();
      } else {
        openSnackbar(t("TXT_DEVICE_LOGOUT_FAIL"), "error");
      }
    },
    onError: () => {
      openSnackbar(t("TXT_DEVICE_LOGOUT_ERROR"), "error");
    },
  });

  // --- Dialog controls ---
  const openBlockDialog = (targetId: string | null = null) => {
    setConfirmDialog({ open: true, type: "block", targetId });
  };

  const openUnblockDialog = (targetId: string | null = null) => {
    setConfirmDialog({ open: true, type: "unblock", targetId });
  };

  const openLogoutDialog = (targetId: string | null = null) => {
    setConfirmDialog({ open: true, type: "logout", targetId });
  };

  const closeDialog = () => {
    setConfirmDialog({ open: false, type: null, targetId: null });
  };

  // --- Build params helpers ---
  function buildBulkParams(bulkParams: BulkParams, targetId: string | null): Record<string, unknown> {
    if (bulkParams.isSelectAllMode && !targetId) {
      return {
        select_all: true,
        exclude_device_ids: bulkParams.excludeDeviceIds,
        search: bulkParams.searchText || undefined,
        device_type: bulkParams.deviceTypeParam || undefined,
        device_version:
          bulkParams.selectedVersions && !bulkParams.selectedVersions.includes("all")
            ? bulkParams.selectedVersions[0]
            : undefined,
      };
    }
    const ids = targetId ? [targetId] : bulkParams.selectedDeviceIds;
    return { device_ids: ids };
  }

  function buildLogoutParams(bulkParams: BulkParams | undefined, targetId: string | null): Record<string, unknown> {
    if (bulkParams?.isSelectAllMode && !targetId) {
      return {
        select_all: true,
        exclude_device_ids: bulkParams.excludeDeviceIds,
        search: bulkParams.searchText || undefined,
        device_type: bulkParams.deviceTypeParam || undefined,
        device_version:
          bulkParams.selectedVersions && !bulkParams.selectedVersions.includes("all")
            ? bulkParams.selectedVersions[0]
            : undefined,
      };
    }
    if (targetId) {
      return { target_device_id: targetId };
    }
    return { device_ids: bulkParams?.selectedDeviceIds || [] };
  }

  // --- Confirm action ---
  const confirmAction = (bulkParams?: BulkParams) => {
    const { type, targetId } = confirmDialog;

    if (type === "block") {
      const params = bulkParams ? buildBulkParams(bulkParams, targetId) : { device_ids: [targetId] };
      blockMutation.mutate(params);
    } else if (type === "unblock") {
      const params = bulkParams ? buildBulkParams(bulkParams, targetId) : { device_ids: [targetId] };
      unblockMutation.mutate(params);
    } else if (type === "logout") {
      const params = buildLogoutParams(bulkParams, targetId);
      logoutMutation.mutate(params);
    }

    closeDialog();
  };

  const getDialogTitle = (selectedCount?: number): string => {
    const { type, targetId } = confirmDialog;
    if (type === "logout") {
      return targetId
        ? t("TXT_DEVICE_CONFIRM_LOGOUT_SINGLE")
        : t("FM_DEVICE_CONFIRM_LOGOUT_BULK", { count: selectedCount || 0 });
    }
    if (type === "block") {
      return targetId
        ? t("TXT_DEVICE_CONFIRM_BLOCK_SINGLE")
        : t("FM_DEVICE_CONFIRM_BLOCK_BULK", { count: selectedCount || 0 });
    }
    if (type === "unblock") {
      return targetId
        ? t("TXT_DEVICE_CONFIRM_UNBLOCK_SINGLE")
        : t("FM_DEVICE_CONFIRM_UNBLOCK_BULK", { count: selectedCount || 0 });
    }
    return "";
  };

  return {
    confirmDialog,
    openBlockDialog,
    openUnblockDialog,
    openLogoutDialog,
    closeDialog,
    confirmAction,
    getDialogTitle,
    isLoading: blockMutation.isLoading || unblockMutation.isLoading || logoutMutation.isLoading,
  };
}
